import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Modal,
  Body,
  Spacer,
  MediaObject,
  Image,
  ButtonToggleGroup,
  ButtonToggle,
  LoadingIndicator,
  ButtonLayout,
  Button,
  FlagText,
  ProductTile,
  Chip,
  ChipGroup
} from "@common/AggregationComponent";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  CombinedReducersType,
  L2,
  ItemWishListMainData,
  BRAND_LIST
} from "@type/index";
import {
  isNotEmptyObj,
  transformBrandForGoogleAnalytics,
  getEcRenewalCompatibility,
  getEcRenewalImageRatio
} from "@util/common";
import { isGUNativeApp } from "@util/nativeApp";
import { FavoritesItemActions } from "@redux/reducer";
import {
  sendTrackEvent,
  TRACK_EVENT,
  trackAddToWishListEvent,
  trackRemoveFromWishListEvent
} from "@util/gtm";
import { COUNTRY } from "@type/country";

const FavoritesPopup = (props: any): any => {
  const { t } = useTranslation();
  const { closePopup, productInfo, modelWearingColor } = props;
  const dispatch = useDispatch();
  const config = useSelector((store: CombinedReducersType) => store.app);
  const favoritesItemData = useSelector(
    (store: CombinedReducersType) => store.favoritesItem
  );

  const [colorCode, setColorCode] = useState<string>("");
  useEffect(() => {
    if (productInfo.productId && productInfo.priceGroup) {
      if (isGUNativeApp()) {
        // To get SKU details without being logged in
        dispatch(
          FavoritesItemActions.fetchProductL2sWithImage({
            productId: productInfo.productId,
            priceGroup: productInfo.priceGroup
          })
        );
      } else {
        dispatch(
          FavoritesItemActions.fetchProductSKUs({
            productId: productInfo.productId,
            priceGroup: productInfo.priceGroup
          })
        );
      }
    }
  }, [dispatch, productInfo.priceGroup, productInfo.productId]);

  useEffect(() => {
    if (productInfo.color && productInfo.color.code) {
      setColorCode(productInfo.color.code);
    }
  }, [productInfo.color]);

  useEffect(() => {
    if (config.isVisibleLogin) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.isVisibleLogin]);

  const onWishlist = useMemo(
    () => (size: L2) => {
      if (!size.l2Id || !productInfo.productId || !productInfo.priceGroup) {
        return;
      }
      if (size.isWishlisted) {
        sendTrackEvent({
          brand: transformBrandForGoogleAnalytics(config.brand_name),
          event: TRACK_EVENT,
          eventAction: "remove_from_wishlist",
          eventCategory: "IS_Item_Wishlist_item",
          eventLabel: `${size.l2Id}`
        });
        // remove_from_wishlist GA event
        trackRemoveFromWishListEvent({
          brand: config.brand_name as BRAND_LIST,
          country: config.region as COUNTRY,
          productData: {
            ...productInfo,
            itemId: size.l2Id,
            wishColorName: size?.color?.name,
            wishColorCode: size?.color?.displayCode,
            wishSize: size?.size?.name,
            wishSizeCode: size?.size?.displayCode,
            imageUrl: size.imageUrl,
            pld: size.pld
          }
        });
        dispatch(
          FavoritesItemActions.delFavoritesItem({
            id: size.l2Id,
            isGUNativeApp: isGUNativeApp()
          })
        );
      } else {
        sendTrackEvent({
          brand: transformBrandForGoogleAnalytics(config.brand_name),
          event: TRACK_EVENT,
          eventAction: "add_to_wishlist",
          eventCategory: "IS_Item_Wishlist_item",
          eventLabel: `${size.l2Id}`
        });
        // add_to_wishlist GA event
        trackAddToWishListEvent({
          brand: config.brand_name as BRAND_LIST,
          country: config.region as COUNTRY,
          productData: {
            ...productInfo,
            itemId: size.l2Id,
            wishColorName: size?.color?.name,
            wishColorCode: size?.color?.displayCode,
            wishSize: size?.size?.name,
            wishSizeCode: size?.size?.displayCode,
            imageUrl: size.imageUrl,
            pld: size.pld
          }
        });
        dispatch(
          FavoritesItemActions.postFavoritesItem({
            body: [
              {
                l2Id: size.l2Id,
                isRepresentative: false
              }
            ],
            isGUNativeApp: isGUNativeApp()
          })
        );
      }
    },
    [
      config.brand_name,
      dispatch,
      productInfo.priceGroup,
      productInfo.productId,
      favoritesItemData.currentItemInfo.stylingId
    ]
  );

  const [selectedColor, setSelectedColor] = useState(modelWearingColor);

  const filteredSKUs = useMemo(() => {
    // Skip hidden color and SKU if there are more than one SKU
    // for length == 1 case, we only check showFlag of color
    // for length > 1, we check both color and size
    const colorGroups = JSON.parse(JSON.stringify(favoritesItemData.allL2s));
    Object.keys(colorGroups).forEach((colorId) => {
      if (
        colorGroups[colorId].length === 1 &&
        !colorGroups[colorId][0].color?.display.showFlag
      ) {
        delete colorGroups[colorId];
      } else if (colorGroups[colorId].length > 1) {
        colorGroups[colorId] = colorGroups[colorId].filter(
          (sku: L2) =>
            sku.color?.display?.showFlag && sku.size?.display?.showFlag
        );
        if (!colorGroups[colorId].length) {
          delete colorGroups[colorId];
        }
      }
    });
    return colorGroups;
  }, [favoritesItemData.allL2s]);

  const processedAllL2s = useMemo(() => {
    const l2s: typeof filteredSKUs = {};
    Object.keys(filteredSKUs).forEach((color) => {
      l2s[color] = filteredSKUs[color].map((size: L2) => ({
        ...size,
        isWishlisted: favoritesItemData.favoritesItemList.some(
          (l2Id: any) => l2Id === size.l2Id
        )
      }));
    });

    if (getEcRenewalCompatibility()) {
      return l2s;
    } else {
      // The favorite modals are sorted by color number, with the color worn at the top of the list
      let obj: typeof filteredSKUs = {};
      if (!favoritesItemData.skusLoading && isNotEmptyObj(l2s)) {
        if (colorCode && l2s[colorCode]) {
          obj = {
            [colorCode]: [],
            ...l2s
          };
        } else {
          obj = { ...l2s };
        }
      }
      return obj;
    }
  }, [
    colorCode,
    favoritesItemData.favoritesItemList,
    favoritesItemData.skusLoading,
    filteredSKUs
  ]);

  const SKUList = useMemo(
    () =>
      Object.keys(processedAllL2s).map((color) => (
        <Spacer element="div" variant="spacing-05" key={color}>
          <MediaObject
            media={
              <Image
                alt="Trench coat"
                ratio="1x1"
                src={processedAllL2s[color][0].imageUrl || ""}
                noImage={{
                  imageSize: "large",
                  imageType: "product",
                  noImageLabel: t("text_no_image")
                }}
              />
            }
            mediaPosition="left"
            smallContainer="horizontal"
            variation="large"
            className="fr-ec-mb-spacing-03"
          >
            <FlagText element="p" color="primary-dark" variant="large">
              {t("text_color")}: {processedAllL2s[color][0].color?.displayCode}{" "}
              {processedAllL2s[color][0].color?.name}
            </FlagText>
          </MediaObject>
          <ButtonToggleGroup
            columnCount={processedAllL2s[color].length > 1 ? 2 : 1}
          >
            {processedAllL2s[color].map((size: L2) => (
              <ButtonToggle
                key={size.l2Id}
                icon="heart"
                onClick={() => {
                  if (!favoritesItemData.itemLoading) {
                    onWishlist(size);
                  }
                }}
                text={
                  size.pld?.display?.showFlag
                    ? `${size.size?.name}${t("text_size_multiplication_sign")}${
                        size.pld?.name
                      }`
                    : size.size?.name
                }
                variant="labelWithIcon"
                isToggled={!!size.isWishlisted}
              />
            ))}
          </ButtonToggleGroup>
        </Spacer>
      )),
    [favoritesItemData.itemLoading, onWishlist, processedAllL2s, t]
  );

  const handleClose = () => {
    closePopup(false);
    dispatch(FavoritesItemActions.fetchProductSKUsFail(false));
    dispatch(FavoritesItemActions.initFavoritesItem());
  };

  const chipImages: { [color: string]: string } = favoritesItemData.images
    ? favoritesItemData.images.chip
    : {};
  const mainImages: {
    [color: string]: ItemWishListMainData;
  } = favoritesItemData.images ? favoritesItemData.images.main : {};

  // const getModalFooter = () => (
  //   <Spacer className="fr-ec-mt-spacing-02" element="div" variant="spacing-05">
  //     <ButtonLayout>
  //       <Button element="button" variant="primary" onClick={handleClose}>
  //         {t("text_close")}
  //       </Button>
  //     </ButtonLayout>
  //   </Spacer>
  // );

  //??? <StyleUserInfo/>
  const getModalBody = () => {
    if (favoritesItemData.skusLoading) {
      return <LoadingIndicator variant="normal" />;
    }
    if (
      favoritesItemData.sysError ||
      (getEcRenewalCompatibility() && Object.keys(chipImages).length === 0) ||
      Object.keys(processedAllL2s).length === 0
    ) {
      return (
        <>
          <Body className="fr-ec-mb-spacing-05" element="p">
            {t("text_error_description")}
          </Body>
        </>
      );
    } else {
      return (
        <>{getEcRenewalCompatibility() ? getPhase3ModalContent() : SKUList}</>
      );
    }
  };

  const getPhase3ModalContent = useCallback(() => {
    const colorChipsContent = Object.keys(processedAllL2s).map((colorId) => (
      <Chip
        label=""
        image={
          chipImages[processedAllL2s[colorId][0]?.color?.displayCode] || ""
        }
        variant="default"
        type="radio"
        name="colorDisplay"
        ecRenewal
        value={colorId}
        key={colorId}
        onChange={() => setSelectedColor(colorId)}
        selected={colorId === selectedColor}
      />
    ));

    return (
      <>
        {selectedColor && processedAllL2s[selectedColor] && (
          <ProductTile
            variant="horizontal-small"
            name=""
            noImageLabel={t("text_no_image")}
            ecRenewal
            favoriteButtonHidden
            media={
              <Image
                src={
                  mainImages[
                    processedAllL2s[selectedColor][0]?.color?.displayCode
                  ]?.image || ""
                }
                noImage={{
                  imageSize: "default",
                  imageType: "product",
                  noImageLabel: t("text_no_image")
                }}
                ratio={getEcRenewalImageRatio()}
              />
            }
            informationContent={
              <FlagText element="p" color="primary-dark" variant="large">
                {`${t("text_color")}: ${
                  processedAllL2s[selectedColor][0].color?.displayCode
                } ${processedAllL2s[selectedColor][0].color?.name}`}
              </FlagText>
            }
            className="fr-ec-mb-spacing-04"
          />
        )}
        <ChipGroup
          id="favModalColorDisplay"
          ecRenewal
          variant="productSelector"
          className="fr-ec-mb-spacing-04"
          swipeable={{ sm: false, md: false, lg: false }}
        >
          {colorChipsContent}
        </ChipGroup>
        {selectedColor && processedAllL2s[selectedColor] && (
          <Spacer variant="spacing-02" element="div">
            <ButtonToggleGroup columnCount={2}>
              {processedAllL2s[selectedColor].map((size: any) => (
                <ButtonToggle
                  key={size.l2Id}
                  icon="heart"
                  onClick={() => onWishlist(size)}
                  text={
                    size.pld?.display?.showFlag
                      ? `${size.size?.name}${t(
                          "text_size_multiplication_sign"
                        )}${size.pld?.name}`
                      : size.size?.name
                  }
                  variant="labelWithIcon"
                  isToggled={!!size.isWishlisted}
                />
              ))}
            </ButtonToggleGroup>
          </Spacer>
        )}
      </>
    );
  }, [processedAllL2s, onWishlist, selectedColor, chipImages, mainImages]);

  return (
    <Modal
      closeTitle={t("text_close")}
      closeLabel={getEcRenewalCompatibility() ? " " : t("text_a11y_close")}
      isOpen={isGUNativeApp() ? true : !config.isVisibleLogin}
      onRequestClose={handleClose}
      labelElement="h5"
      title={t("text_a11y_favorites_add")}
      variant="large"
      footer={
        <ButtonLayout variant="fluid">
          <Button
            element="button"
            variant="primary"
            onClick={handleClose}
            ecRenewal={getEcRenewalCompatibility()}
          >
            {t("text_close")}
          </Button>
        </ButtonLayout>
      }
    >
      {getModalBody()}
    </Modal>
  );
};

export default FavoritesPopup;
